import { FormEvent, useMemo, useState } from 'react';
import {
  ArrowRight,
  Building2,
  Check,
  CloudCog,
  Gauge,
  Globe2,
  Layers3,
  LockKeyhole,
  Network,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Users,
  Workflow,
  X,
} from 'lucide-react';

import {
  onboardingClient,
  portalStartUrl,
  type CompanyDraft,
  type OnboardingClient,
} from './onboarding';
import { UserDocumentationSite } from './UserDocs';
import { BrandMark, ConnectPlaneBrand, ConnectPlaneShell } from './ConnectPlaneShell';
import { apiChangelogUrl, developerDocumentationUrl } from './documentation';

const initialDraft: CompanyDraft = {
  companyName: '',
  website: '',
  employeeCountBand: '',
  companyType: '',
  countryCode: 'US',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  intendedUse: '',
};

const capabilities = [
  {
    icon: Network,
    title: 'Model every connection',
    copy: 'Bring customers, services, locations, routes, and infrastructure into one operational context.',
  },
  {
    icon: Workflow,
    title: 'Automate the service lifecycle',
    copy: 'Move from qualification through order, delivery, and support with workflows your teams can trust.',
  },
  {
    icon: Gauge,
    title: 'Operate with live signals',
    copy: 'See platform health, service status, and customer impact without stitching together isolated dashboards.',
  },
  {
    icon: Layers3,
    title: 'Grow a digital ecosystem',
    copy: 'Connect sellers, partners, catalogs, and tenant workspaces while preserving clear data boundaries.',
  },
];

const outcomes = [
  'Launch new service experiences without rebuilding the control plane',
  'Give operations and commercial teams one shared source of context',
  'Keep tenant, identity, and data boundaries explicit as you scale',
];

type ConnectPlaneSiteProps = {
  client?: OnboardingClient;
  onHandoff?: (sessionId: string) => void;
};

function Field({
  label,
  name,
  value,
  onChange,
  required,
  type = 'text',
  placeholder,
}: {
  label: string;
  name: keyof CompanyDraft;
  value: string;
  onChange: (name: keyof CompanyDraft, value: string) => void;
  required?: boolean;
  type?: string;
  placeholder?: string;
}) {
  return (
    <label className="field">
      <span>{label}{required ? ' *' : ''}</span>
      <input
        name={name}
        value={value}
        onChange={(event) => onChange(name, event.target.value)}
        required={required}
        type={type}
        placeholder={placeholder}
      />
    </label>
  );
}

function OnboardingWizard({
  client,
  onHandoff,
  onClose,
}: {
  client: OnboardingClient;
  onHandoff: (sessionId: string) => void;
  onClose: () => void;
}) {
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState(initialDraft);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const update = (name: keyof CompanyDraft, value: string) => {
    setDraft((current) => ({ ...current, [name]: value }));
    setError('');
  };

  const stepValid = useMemo(() => {
    if (step === 0) return Boolean(draft.companyName.trim() && draft.employeeCountBand && draft.companyType);
    if (step === 1) return Boolean(draft.contactName.trim() && /^\S+@\S+\.\S+$/.test(draft.contactEmail.trim()));
    return true;
  }, [draft, step]);

  const next = () => {
    if (!stepValid) {
      setError('Complete the required fields before continuing.');
      return;
    }
    setStep((current) => Math.min(current + 1, 2));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (step < 2) {
      next();
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const session = await client.createSession(draft);
      onHandoff(session.id);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to start onboarding. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="onboarding-layer" role="presentation">
      <button className="onboarding-scrim" aria-label="Close onboarding" onClick={onClose} />
      <section className="onboarding-panel" role="dialog" aria-modal="true" aria-labelledby="onboarding-title">
        <header className="onboarding-header">
          <div>
            <p className="eyebrow">Your ConnectPlane workspace</p>
            <h2 id="onboarding-title">Let’s shape your starting point.</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close onboarding">
            <X aria-hidden="true" />
          </button>
        </header>

        <ol className="steps" aria-label="Onboarding progress">
          {['Company', 'Contact', 'Review'].map((label, index) => (
            <li key={label} className={index === step ? 'active' : index < step ? 'complete' : ''}>
              <span>{index < step ? <Check aria-hidden="true" /> : index + 1}</span>
              {label}
            </li>
          ))}
        </ol>

        <form onSubmit={submit} noValidate>
          {step === 0 ? (
            <div className="form-grid">
              <Field label="Company name" name="companyName" value={draft.companyName} onChange={update} required placeholder="Northstar Networks" />
              <Field label="Website" name="website" value={draft.website} onChange={update} type="url" placeholder="https://example.com" />
              <label className="field">
                <span>Company size *</span>
                <select value={draft.employeeCountBand} onChange={(event) => update('employeeCountBand', event.target.value)} required>
                  <option value="">Choose a range</option>
                  <option value="1-10">1–10</option>
                  <option value="11-50">11–50</option>
                  <option value="51-250">51–250</option>
                  <option value="251-1000">251–1,000</option>
                  <option value="1000+">1,000+</option>
                </select>
              </label>
              <label className="field">
                <span>Organization type *</span>
                <select value={draft.companyType} onChange={(event) => update('companyType', event.target.value)} required>
                  <option value="">Choose a type</option>
                  <option value="telecom_operator">Network operator or carrier</option>
                  <option value="telecom_reseller">Service provider or reseller</option>
                  <option value="systems_integrator">Systems integrator</option>
                  <option value="enterprise_buyer">Enterprise team</option>
                  <option value="other">Other</option>
                </select>
              </label>
            </div>
          ) : null}

          {step === 1 ? (
            <div className="form-grid">
              <Field label="Your name" name="contactName" value={draft.contactName} onChange={update} required placeholder="Jordan Lee" />
              <Field label="Work email" name="contactEmail" value={draft.contactEmail} onChange={update} required type="email" placeholder="jordan@example.com" />
              <Field label="Phone" name="contactPhone" value={draft.contactPhone} onChange={update} type="tel" placeholder="Optional" />
              <label className="field">
                <span>Country *</span>
                <select value={draft.countryCode} onChange={(event) => update('countryCode', event.target.value)} required>
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="GB">United Kingdom</option>
                  <option value="AU">Australia</option>
                  <option value="NZ">New Zealand</option>
                  <option value="OTHER">Other</option>
                </select>
              </label>
            </div>
          ) : null}

          {step === 2 ? (
            <div className="review-step">
              <label className="field">
                <span>What do you want to accomplish first?</span>
                <textarea
                  value={draft.intendedUse}
                  onChange={(event) => update('intendedUse', event.target.value)}
                  rows={4}
                  placeholder="Tell us about the first service, workflow, or team you want to bring into ConnectPlane."
                />
              </label>
              <div className="review-card">
                <div><span>Company</span><strong>{draft.companyName}</strong></div>
                <div><span>Team</span><strong>{draft.employeeCountBand} employees</strong></div>
                <div><span>Contact</span><strong>{draft.contactEmail}</strong></div>
              </div>
              <p className="privacy-note"><LockKeyhole aria-hidden="true" /> Your draft is saved server-side and continues securely through account registration.</p>
            </div>
          ) : null}

          {error ? <p className="form-error" role="alert">{error}</p> : null}

          <footer className="form-actions">
            {step > 0 ? <button type="button" className="button secondary" onClick={() => setStep((current) => current - 1)}>Back</button> : <span />}
            <button type="submit" className="button primary" disabled={submitting}>
              {submitting ? <><RefreshCw className="spin" aria-hidden="true" /> Saving…</> : step === 2 ? <>Continue to account setup <ArrowRight aria-hidden="true" /></> : <>Continue <ArrowRight aria-hidden="true" /></>}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}

export function ConnectPlaneSite({ client = onboardingClient, onHandoff }: ConnectPlaneSiteProps) {
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const handoff = onHandoff ?? ((sessionId: string) => window.location.assign(portalStartUrl(sessionId)));
  const signInUrl = import.meta.env.VITE_PORTAL_SIGN_IN_URL || 'https://myaccount.connectplane.com/';
  const docsUrl = developerDocumentationUrl(window.location.hostname);
  const footer = <footer className="site-footer"><a className="brand" href="#top"><ConnectPlaneBrand sectionLabel="by ACD" /></a><p>Connected systems. Clearer service.</p><a href="/docs">User guides</a><a href="/docs/changelog">Portal updates</a><a href={docsUrl}>API docs</a><a href={apiChangelogUrl(window.location.hostname)}>API changelog</a><span>© {new Date().getFullYear()} ACD</span></footer>;

  return (
    <ConnectPlaneShell
      surface="marketing"
      sectionLabel="by ACD"
      brandHref="#top"
      brandLabel="ConnectPlane home"
      mainContentId="main"
      topbarActions={
        <div className="header-actions">
          <a className="text-link" href={signInUrl}>Sign in</a>
          <button className="button primary compact" onClick={() => setOnboardingOpen(true)}>Start onboarding <ArrowRight aria-hidden="true" /></button>
        </div>
      }
      footer={footer}
    >

      <main id="main" className="marketing-main">
        <section className="hero" id="top">
          <div className="hero-copy">
            <p className="eyebrow"><Sparkles aria-hidden="true" /> One connected operating plane</p>
            <h1>Turn complex service delivery into clear momentum.</h1>
            <p className="hero-lede">ConnectPlane brings commerce, operations, infrastructure, and customer experience into one SaaS platform—so every team can act from the same context.</p>
            <div className="hero-actions">
              <button className="button primary" onClick={() => setOnboardingOpen(true)}>Build your starting plan <ArrowRight aria-hidden="true" /></button>
              <a className="button secondary" href="#platform">Explore the platform</a>
            </div>
            <div className="proof-line"><ShieldCheck aria-hidden="true" /><span>Tenant-aware by design</span><span>API-first</span><span>Observable end to end</span></div>
          </div>
          <div className="plane-visual" aria-label="Connected service lifecycle illustration">
            <div className="visual-orbit orbit-one"><span>Commerce</span></div>
            <div className="visual-orbit orbit-two"><span>Operations</span></div>
            <div className="visual-core"><BrandMark /><strong>ConnectPlane</strong><small>Shared context</small></div>
            <div className="signal signal-a" /><div className="signal signal-b" /><div className="signal signal-c" />
          </div>
        </section>

        <section className="section capabilities" id="platform">
          <div className="section-heading">
            <p className="eyebrow">Platform</p>
            <h2>One plane for the work between systems.</h2>
            <p>ConnectPlane connects the decisions, handoffs, and signals that usually disappear between specialized tools.</p>
          </div>
          <div className="capability-grid">
            {capabilities.map(({ icon: Icon, title, copy }) => (
              <article key={title} className="capability-card"><Icon aria-hidden="true" /><h3>{title}</h3><p>{copy}</p><span>Explore capability <ArrowRight aria-hidden="true" /></span></article>
            ))}
          </div>
        </section>

        <section className="section outcomes" id="outcomes">
          <div className="outcomes-panel">
            <div>
              <p className="eyebrow">Designed for compound progress</p>
              <h2>Start with one workflow. Keep the context you create.</h2>
              <p>ConnectPlane gives growing service organizations a durable operating model instead of another isolated point solution.</p>
            </div>
            <ul>{outcomes.map((outcome) => <li key={outcome}><Check aria-hidden="true" />{outcome}</li>)}</ul>
          </div>
          <div className="metric-row">
            <div><strong>01</strong><span>Shared customer and service context</span></div>
            <div><strong>02</strong><span>Governed tenant and partner boundaries</span></div>
            <div><strong>03</strong><span>Observable workflows from edge to outcome</span></div>
          </div>
        </section>

        <section className="section trust" id="trust">
          <div className="section-heading"><p className="eyebrow">Built for trust</p><h2>Control without slowing down.</h2></div>
          <div className="trust-grid">
            <article><LockKeyhole aria-hidden="true" /><h3>Identity-led access</h3><p>Explicit tenant context and policy-aware access keep every interaction scoped.</p></article>
            <article><CloudCog aria-hidden="true" /><h3>Portable operations</h3><p>Cloud-native delivery and open interfaces keep the platform adaptable.</p></article>
            <article><Globe2 aria-hidden="true" /><h3>Connected visibility</h3><p>Telemetry follows the workflow so teams can understand impact, not just uptime.</p></article>
            <article><Users aria-hidden="true" /><h3>Partner-ready</h3><p>Share capabilities across organizations without flattening ownership boundaries.</p></article>
          </div>
        </section>

        <section className="cta-section">
          <Building2 aria-hidden="true" />
          <div><p className="eyebrow">Your first connection</p><h2>Tell us where you want to begin.</h2><p>We’ll save your starting context and carry it into a secure ConnectPlane account application.</p></div>
          <button className="button primary" onClick={() => setOnboardingOpen(true)}>Start onboarding <ArrowRight aria-hidden="true" /></button>
        </section>
      </main>

      {onboardingOpen ? <OnboardingWizard client={client} onHandoff={handoff} onClose={() => setOnboardingOpen(false)} /> : null}
    </ConnectPlaneShell>
  );
}

export type ConnectPlaneSurface = 'site' | 'user-docs' | 'developer-docs-redirect';

export function connectPlaneSurface(hostname: string, path: string): ConnectPlaneSurface {
  const developerDocsPath = path === '/changelog' || path === '/docs/api' || path.startsWith('/docs/api/');
  if (developerDocsPath) return 'developer-docs-redirect';
  if (path === '/docs' || path.startsWith('/docs/')) return 'user-docs';
  return 'site';
}

export default function App() {
  const surface = connectPlaneSurface(window.location.hostname, window.location.pathname);
  if (surface === 'developer-docs-redirect') {
    window.location.replace(developerDocumentationUrl(window.location.hostname, window.location.pathname));
    return null;
  }
  if (surface === 'user-docs') {
    return <UserDocumentationSite />;
  }
  return <ConnectPlaneSite />;
}
